import React, {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AddSchedulingContactResponse,
  CircleOrganisation,
  ListSchedulingContactsResponse as BaseListResponse,
  RemoveSchedulingContactResponse,
  SchedulingContact as BaseSchedulingContact,
  UpdateSchedulingContactResponse,
} from '@dreamai/types';
import api from '../../api';
import BottomSheet from '../common/BottomSheet';
import './scheduling-contacts-section.scss';

// Until @dreamai/types carries the shop fields (dream-types #109).
type SchedulingContact = BaseSchedulingContact & {
  contactUserId?: string | null;
  shops?: string[];
};
interface CircleShop {
  id: string;
  name: string;
}
interface CircleShopMember {
  userId: string;
  name: string | null;
  shops: string[];
}
type ListSchedulingContactsResponse = BaseListResponse & {
  contacts: SchedulingContact[];
  shops?: CircleShop[];
  shopMembers?: CircleShopMember[];
};
// Until @dreamai/types carries `invite` on AddSchedulingContactResponse.
interface CircleEmailInvite {
  url: string;
  code: string;
  expiresAt: string;
  name: string | null;
}
type AddContactResponse = AddSchedulingContactResponse & {
  invite?: CircleEmailInvite;
};
interface ShopMembershipResponse {
  success: boolean;
  error?: string;
}

const SHOP_ICON = (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z' />
    <path d='M3 6h18M16 10a4 4 0 0 1-8 0' />
  </svg>
);

function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

interface SchedulingContactsSectionProps {
  assistantName?: string;
}

type Permission = 'allowScheduling' | 'allowHeadsUps';

const PERMISSIONS: {
  key: Permission;
  tone: 'sch' | 'awt';
  label: string;
  detail: string;
  icon: ReactElement;
}[] = [
  {
    key: 'allowScheduling',
    tone: 'sch',
    label: 'Scheduling',
    detail:
      'Their secretary can ask yours to find a time to meet. You still say yes to every meeting.',
    icon: (
      <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
      >
        <rect x='3' y='5' width='18' height='16' rx='3' />
        <path d='M3 10h18M8 3v4M16 3v4' />
      </svg>
    ),
  },
  {
    key: 'allowHeadsUps',
    tone: 'awt',
    label: 'Messages',
    detail:
      'Their secretary can pass yours a message, question or document for you.',
    icon: (
      <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
      >
        <path d='M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z' />
        <path d='M9 11h6M9 14h4' />
      </svg>
    ),
  },
];

function labelOf(contact: SchedulingContact): string {
  return contact.contactName || contact.contactEmail || 'Unknown contact';
}

function initialsOf(label: string): string {
  const words = label
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters = words.slice(0, 2).map((w) => w[0]);
  return (letters.join('') || label[0] || '?').toUpperCase();
}

function subtitleOf(contact: SchedulingContact): string {
  if (contact.group)
    return `You're in each other's Circles, via ${contact.group}`;
  if (contact.bothWays) return "You're in each other's Circles";
  if (contact.contactName && contact.contactEmail) return contact.contactEmail;
  return 'Added by you';
}

function untilOf(contact: SchedulingContact): string {
  if (!contact.expiresAt) return 'Permanent';
  return `Until ${new Date(contact.expiresAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  })}`;
}

const SchedulingContactsSection: FC<SchedulingContactsSectionProps> = ({
  assistantName,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const paName = assistantName || 'your secretary';

  const [contacts, setContacts] = useState<SchedulingContact[]>([]);
  const [organisation, setOrganisation] = useState<CircleOrganisation | null>(
    null,
  );
  const [shops, setShops] = useState<CircleShop[]>([]);
  const [shopMembers, setShopMembers] = useState<CircleShopMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [invite, setInvite] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
    about: 'people' | 'add';
  } | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<ListSchedulingContactsResponse>(
        '/scheduling-contacts',
        await authHeaders(),
      );
      if (data.success) {
        setContacts(data.contacts);
        setOrganisation(data.organisation ?? null);
        setShops(data.shops ?? []);
        setShopMembers(data.shopMembers ?? []);
      }
    } catch (err) {
      console.error('Error loading Circle:', err);
      setFeedback({
        type: 'error',
        about: 'people',
        message: 'Could not load your Circle.',
      });
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = useCallback(async () => {
    const email = newEmail.trim();
    if (!email) return;
    setAdding(true);
    setFeedback(null);
    setInvite(null);
    setCopied(false);
    try {
      const { data } = await api.post<AddContactResponse>(
        '/scheduling-contacts',
        { contactEmail: email },
        await authHeaders(),
      );
      if (data.success && data.contact) {
        const contact = data.contact;
        setContacts((prev) =>
          prev.some((c) => c.id === contact.id) ? prev : [...prev, contact],
        );
        setNewEmail('');
        if (data.invite) {
          setInvite({
            url: data.invite.url,
            name: data.invite.name || labelOf(contact),
          });
          return;
        }
        setFeedback({
          type: 'success',
          about: 'add',
          message: data.created
            ? `${labelOf(contact)} is in your Circle. Tap them to change what they're allowed.`
            : `${labelOf(contact)} is already in your Circle.`,
        });
      } else {
        setFeedback({
          type: 'error',
          about: 'add',
          message: data.error || 'Could not add that person.',
        });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setFeedback({
        type: 'error',
        about: 'add',
        message:
          axiosErr?.response?.data?.error ||
          'Could not add that person. Please try again.',
      });
    } finally {
      setAdding(false);
    }
  }, [newEmail, authHeaders]);

  const handleCopyInvite = useCallback(async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [invite]);

  const handleToggle = useCallback(
    async (contact: SchedulingContact, key: Permission) => {
      const other: Permission =
        key === 'allowScheduling' ? 'allowHeadsUps' : 'allowScheduling';
      if (contact[key] && !contact[other]) {
        setFeedback({
          type: 'error',
          about: 'people',
          message: `${labelOf(contact)} needs at least one of these. To take them out of your Circle, use Remove.`,
        });
        return;
      }
      setBusyId(contact.id);
      setFeedback(null);
      try {
        const { data } = await api.patch<UpdateSchedulingContactResponse>(
          `/scheduling-contacts/${contact.id}`,
          { [key]: !contact[key] },
          await authHeaders(),
        );
        if (data.success && data.contact) {
          const updated = data.contact;
          setContacts((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c)),
          );
        } else {
          setFeedback({
            type: 'error',
            about: 'people',
            message: data.error || 'Could not change that.',
          });
        }
      } catch (err) {
        console.error('Error updating Circle member:', err);
        setFeedback({
          type: 'error',
          about: 'people',
          message: 'Could not change that. Please try again.',
        });
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders],
  );

  const handleRemove = useCallback(
    async (contact: SchedulingContact) => {
      setBusyId(contact.id);
      setFeedback(null);
      try {
        const { data } = await api.delete<RemoveSchedulingContactResponse>(
          `/scheduling-contacts/${contact.id}`,
          await authHeaders(),
        );
        if (data.success) {
          setContacts((prev) => prev.filter((c) => c.id !== contact.id));
          setOpenId(null);
          setFeedback({
            type: 'success',
            about: 'people',
            message: contact.bothWays
              ? `${labelOf(contact)} is out of your Circle, and you're out of theirs.`
              : `${labelOf(contact)} is out of your Circle.`,
          });
        } else {
          setFeedback({
            type: 'error',
            about: 'people',
            message: data.error || 'Could not remove that person.',
          });
        }
      } catch (err) {
        console.error('Error removing Circle member:', err);
        setFeedback({
          type: 'error',
          about: 'people',
          message: 'Could not remove that person. Please try again.',
        });
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders],
  );

  const shopNames = (ids: string[] | undefined) =>
    shops.filter((shop) => ids?.includes(shop.id)).map((shop) => shop.name);

  const handleShop = useCallback(
    async (
      person: { key: string; userId: string; name: string },
      shop: CircleShop,
      join: boolean,
    ) => {
      setBusyId(person.key);
      setFeedback(null);
      const url = `/scheduling-contacts/shops/${shop.id}/members/${person.userId}`;
      try {
        const { data } = join
          ? await api.put<ShopMembershipResponse>(url, {}, await authHeaders())
          : await api.delete<ShopMembershipResponse>(url, await authHeaders());
        if (data.success) {
          await load();
          setFeedback({
            type: 'success',
            about: 'people',
            message: join
              ? `${person.name} is in the ${shop.name}. Their secretary will tell them.`
              : `${person.name} is out of the ${shop.name}. Their secretary will tell them.`,
          });
        } else {
          setFeedback({
            type: 'error',
            about: 'people',
            message: data.error || 'Could not change that.',
          });
        }
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        setFeedback({
          type: 'error',
          about: 'people',
          message:
            axiosErr?.response?.data?.error ||
            'Could not change that. Please try again.',
        });
      } finally {
        setBusyId(null);
      }
    },
    [authHeaders, load],
  );

  const renderShopSwitches = (
    person: { key: string; userId: string; name: string },
    inShops: string[] | undefined,
    busy: boolean,
  ) =>
    shops.map((shop) => {
      const on = !!inShops?.includes(shop.id);
      return (
        <label key={shop.id} className='circle-switch'>
          <span>{shop.name}</span>
          <button
            type='button'
            className={`toggle-switch${on ? ' on' : ''}`}
            onClick={() => handleShop(person, shop, !on)}
            disabled={busy}
            aria-label={`${shop.name} for ${person.name}`}
            role='switch'
            aria-checked={on}
          >
            <span className='toggle-knob' />
          </button>
        </label>
      );
    });

  const renderTags = (
    contact: Pick<SchedulingContact, Permission>,
    time: string,
    inShops?: string[],
  ) => (
    <div className='circle-tags'>
      {PERMISSIONS.filter((p) => contact[p.key]).map((p) => (
        <span key={p.key} className={`circle-tag ${p.tone}`}>
          {p.label}
        </span>
      ))}
      {shopNames(inShops).map((name) => (
        <span key={name} className='circle-tag shop'>
          {name}
        </span>
      ))}
      {time && <span className='circle-tag time'>{time}</span>}
    </div>
  );

  return (
    <div className='scheduling-contacts-section'>
      <div className='scheduling-contacts-header'>
        <h2 className='section-title'>Your Circle</h2>
        <p className='scheduling-contacts-subtitle'>
          The people whose secretaries can work with {paName}. You choose what
          each person is allowed.
        </p>
      </div>

      <div className='circle-card'>
        {PERMISSIONS.map((p) => (
          <div key={p.key} className='circle-perm'>
            <div className={`circle-perm-icon ${p.tone}`}>{p.icon}</div>
            <div>
              <b className='circle-perm-label'>{p.label}</b>
              <p className='circle-perm-detail'>{p.detail}</p>
            </div>
          </div>
        ))}
        {shops.length > 0 && (
          <div className='circle-perm'>
            <div className='circle-perm-icon shop'>{SHOP_ICON}</div>
            <div>
              <b className='circle-perm-label'>
                {listNames(shops.map((shop) => shop.name))}
              </b>
              <p className='circle-perm-detail'>
                Their secretary can add to your shop and comment on its meal
                plan. It doesn't give Scheduling or Messages.
              </p>
            </div>
          </div>
        )}
        <p className='circle-never'>
          {shops.length > 0 ? 'None of these lets' : 'Neither lets'} anyone see
          your calendar, inbox or what {paName} knows about you, and nothing is
          booked or sent without your say.
        </p>
      </div>

      <div className='circle-card'>
        <h3 className='circle-card-title'>Who's in it</h3>
        <ul className='circle-people'>
          {organisation && (
            <li className='circle-person'>
              <div className='circle-row'>
                <div className='circle-avatar org'>
                  {initialsOf(organisation.name)}
                </div>
                <div className='circle-who'>
                  <b>Everyone at {organisation.name}</b>
                  <span>Automatic, while you work there</span>
                  {renderTags(
                    { allowScheduling: true, allowHeadsUps: true },
                    'Always',
                  )}
                </div>
              </div>
            </li>
          )}
          {contacts.map((contact) => {
            const busy = busyId === contact.id;
            const open = openId === contact.id;
            const name = labelOf(contact);
            return (
              <li key={contact.id} className='circle-person'>
                <button
                  className='circle-row tap'
                  onClick={() => setOpenId(open ? null : contact.id)}
                  aria-expanded={open}
                >
                  <div className='circle-avatar'>{initialsOf(name)}</div>
                  <div className='circle-who'>
                    <b>{name}</b>
                    <span>{subtitleOf(contact)}</span>
                    {renderTags(contact, untilOf(contact), contact.shops)}
                  </div>
                  <span
                    className={`circle-chevron${open ? ' open' : ''}`}
                    aria-hidden='true'
                  />
                </button>
                {open && (
                  <div className='circle-edit'>
                    {PERMISSIONS.map((p) => (
                      <label key={p.key} className='circle-switch'>
                        <span>{p.label}</span>
                        <button
                          type='button'
                          className={`toggle-switch${contact[p.key] ? ' on' : ''}`}
                          onClick={() => handleToggle(contact, p.key)}
                          disabled={busy}
                          aria-label={`${p.label} for ${name}`}
                          role='switch'
                          aria-checked={contact[p.key]}
                        >
                          <span className='toggle-knob' />
                        </button>
                      </label>
                    ))}
                    {contact.contactUserId &&
                      renderShopSwitches(
                        {
                          key: contact.id,
                          userId: contact.contactUserId,
                          name,
                        },
                        contact.shops,
                        busy,
                      )}
                    <p className='circle-note'>
                      {contact.bothWays
                        ? `Removing ${name} takes you out of their Circle too.`
                        : `${name} hasn't added you to their Circle, so this only works one way.${contact.contactEmail ? ' Add their email again to get the link for them.' : ''}`}
                      {shopNames(contact.shops).length > 0 &&
                        ` It also takes them out of the ${listNames(shopNames(contact.shops))}.`}
                    </p>
                    <div className='circle-edit-actions'>
                      <button
                        type='button'
                        className='circle-link'
                        onClick={() => handleRemove(contact)}
                        disabled={busy}
                      >
                        {busy ? 'Removing…' : 'Remove from my Circle'}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
          {shopMembers.map((member) => {
            const key = `shop:${member.userId}`;
            const busy = busyId === key;
            const open = openId === key;
            const name = member.name || 'Someone';
            return (
              <li key={key} className='circle-person'>
                <button
                  className='circle-row tap'
                  onClick={() => setOpenId(open ? null : key)}
                  aria-expanded={open}
                >
                  <div className='circle-avatar'>{initialsOf(name)}</div>
                  <div className='circle-who'>
                    <b>{name}</b>
                    <span>Shop only: no Scheduling or Messages</span>
                    {renderTags(
                      { allowScheduling: false, allowHeadsUps: false },
                      '',
                      member.shops,
                    )}
                  </div>
                  <span
                    className={`circle-chevron${open ? ' open' : ''}`}
                    aria-hidden='true'
                  />
                </button>
                {open && (
                  <div className='circle-edit'>
                    {renderShopSwitches(
                      { key, userId: member.userId, name },
                      member.shops,
                      busy,
                    )}
                    <p className='circle-note'>
                      To add them to another shop or give them Scheduling or
                      Messages, ask {paName} to invite them.
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {feedback?.about === 'people' && (
          <p className={`scheduling-contacts-feedback ${feedback.type}`}>
            {feedback.message}
          </p>
        )}
        {loading ? (
          <p className='circle-note'>Loading…</p>
        ) : contacts.length === 0 && shopMembers.length === 0 ? (
          <p className='circle-note'>
            Nobody else yet. Add someone below, or ask {paName} for a code to
            share: whoever uses it joins your Circle, and you join theirs.
          </p>
        ) : (
          <p className='circle-note'>
            Tap someone to change what they're allowed.
          </p>
        )}
      </div>

      <div className='circle-card'>
        <div className='circle-field'>
          <label className='field-label' htmlFor='sc-new-email'>
            Add someone by email
          </label>
          <div className='scheduling-contacts-add-row'>
            <input
              id='sc-new-email'
              type='email'
              className='field-input'
              placeholder='name@example.com'
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
              disabled={adding}
            />
            <button
              className='btn-add'
              onClick={handleAdd}
              disabled={adding || !newEmail.trim()}
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
        {feedback?.about === 'add' && (
          <p className={`scheduling-contacts-feedback ${feedback.type}`}>
            {feedback.message}
          </p>
        )}
      </div>
      <BottomSheet
        open={!!invite}
        onClose={() => setInvite(null)}
        title='One more step'
        className='circle-link-sheet'
      >
        {invite && (
          <>
            <p className='circle-link-sheet-lead'>
              You've added {invite.name} to your Circle. Now they need to add
              you to theirs. Give them this link.
            </p>
            <span className='circle-link-sheet-url'>{invite.url}</span>
            <button
              type='button'
              className='circle-link-sheet-copy'
              onClick={handleCopyInvite}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </>
        )}
      </BottomSheet>
    </div>
  );
};

export default SchedulingContactsSection;
